import * as React from "react";
import { cn } from "@/lib/utils";
import { Textarea, type TextareaProps } from "./textarea";
import { Button } from "./button";
import { Sparkles } from "lucide-react";

import { useAiCopySuggestion, type FieldContext } from "@/hooks/useAiCopySuggestion";

export interface AiTextareaProps extends TextareaProps {
  fieldContext?: FieldContext;
  onSuggestionAccepted?: (suggestion: string) => void;
}

const AiTextarea = React.forwardRef<HTMLTextAreaElement, AiTextareaProps>(
  ({ className, fieldContext, onSuggestionAccepted, value, ...props }, ref) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const { suggestions, isLoading, isOpen, generateSuggestions, dismiss } = useAiCopySuggestion();
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const [keepButtonVisible, setKeepButtonVisible] = React.useState(false);

    React.useEffect(() => {
      setIsPopoverOpen(isOpen);
    }, [isOpen]);
 
    const handleAiClick = async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      
      if (!fieldContext) {
        console.log('[AiTextarea] No fieldContext provided, aborting AI click');
        return;
      }

      console.log('[AiTextarea] AI click', { fieldContext, value });

      // Keep button visible even if input loses focus
      setKeepButtonVisible(true);
      setIsPopoverOpen(true);

      await generateSuggestions({
        ...fieldContext,
        currentValue: value?.toString() || '',
      });
    };

    const handleSuggestionClick = (suggestion: string) => {
      // Apply to parent-controlled state
      if (onSuggestionAccepted) {
        onSuggestionAccepted(suggestion);
      }
      // Also notify any onChange handlers to keep forms in sync
      if (props.onChange) {
        try {
          props.onChange({ target: { value: suggestion } } as any);
        } catch {}
      }
      // Small delay to ensure state updates before closing
      setTimeout(() => {
        dismiss();
        setIsPopoverOpen(false);
        setKeepButtonVisible(false);
      }, 50);
    };

    const handlePopoverClose = () => {
      setIsPopoverOpen(false);
      setKeepButtonVisible(false);
      dismiss();
    };

    return (
      <div className="relative w-full">
        <Textarea
          ref={ref}
          value={value}
          className={cn(fieldContext && "pr-10", className)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setTimeout(() => {
            if (!keepButtonVisible) {
              setIsFocused(false);
            }
          }, 200)}
          {...props}
        />
        
        {fieldContext && (isFocused || keepButtonVisible) && (
          <>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={cn(
                "absolute right-1 top-1 h-8 w-8 text-primary hover:text-primary hover:bg-primary/10 transition-all duration-200",
                (isFocused || keepButtonVisible) ? "opacity-100" : "opacity-0"
              )}
              onClick={handleAiClick}
              disabled={isLoading}
              onMouseDown={(e) => e.preventDefault()}
            >
              <Sparkles className={cn("h-4 w-4", isLoading && "animate-pulse")} />
            </Button>

            {isPopoverOpen && (
              <div
                className="absolute right-0 top-10 z-[1300] w-80 bg-white dark:bg-gray-800 border border-border rounded-md shadow-md p-0 pointer-events-auto"
                onMouseDown={(e) => {
                  // Prevent closing textarea focus when interacting with the panel
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <div className="p-4 space-y-2">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-primary" />
                      <span className="font-semibold text-sm">AI Suggestions</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handlePopoverClose}
                      className="h-6 text-xs"
                    >
                      Dismiss
                    </Button>
                  </div>

                  {isLoading ? (
                    <div className="space-y-2">
                      {[1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className="h-16 bg-muted animate-pulse rounded-md"
                        />
                      ))}
                    </div>
                  ) : suggestions.length > 0 ? (
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {suggestions.map((suggestion, index) => (
                        <button
                          key={index}
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleSuggestionClick(suggestion);
                          }}
                          className="w-full text-left p-3 text-sm rounded-md border border-border hover:bg-accent hover:border-primary transition-all duration-200 cursor-pointer"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground text-center py-4">
                      Click the sparkle icon to generate suggestions
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  }
);

AiTextarea.displayName = "AiTextarea";

export { AiTextarea };
